import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils from '../../../util/dates';
import math from '../../../util/math';
import { getDetailsData } from '../ThroughputHistogramActions';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

const chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'linear',
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    title: {
      text: 'Throughput (Number of work items completed on a day)',
      style: {
        fontSize: '13px'
      }
    },
    plotLines: [],
    events: {}
  },
  yAxis: {
    allowDecimals: false,
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    min: 0,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Frequency (Number of days)',
      style: {
        fontSize: '13px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      color: 'rgba(43,147,185, 0.9)',
      states: {
        hover: {
          color: 'rgba(0,123,170, 0.9)'
        }
      }
    },
    column: {
      pointPadding: 0,
      borderWidth: 0.5,
      groupPadding: 0,
      shadow: false
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {
      if (this.key == 'Plotline') {
        return `${this.point.text}: ${this.y}`;
      }
      const tooltip = `<p style="font-size: 12px; margin: 5px 3px;">Throughput: ${this.x}</p>  <p style="font-size: 12px; margin: 5px 3px 0;">Frequency: ${this.y}</p>`;
      return tooltip;
    }
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};
const masterchartConfig = {
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  chart: {
    reflow: false,
    borderWidth: 0,
    backgroundColor: null,
    marginLeft: 70,
    marginRight: 10,
    zoomType: 'x',
    events: {}
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    showLastTickLabel: true,
    plotBands: [
      {
        id: 'mask-before',
        color: 'rgba(0, 0, 0, 0.1)'
      }
    ],
    title: {
      text: null
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    }
  },
  yAxis: {
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Daily Throughput',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    }
  },
  plotOptions: {
    series: {
      lineWidth: 2,
      marker: {
        enabled: false
      },
      shadow: false,
      states: {
        hover: {
          lineWidth: 1
        }
      },
      enableMouseTracking: false
    }
  },
  tooltip: {
    formatter: function() {
      return false;
    }
  },
  legend: {
    enabled: false
  },
  series: [
    {
      name: 'Data',
      color: '#0086b3',
      type: 'spline',
      data: []
    }
  ]
};

class ThroughputHistogramChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};

    this.onSelection = this.onSelection.bind(this);
    this.updateDetails = this.updateDetails.bind(this);
  }

  calculatePercentiles = data => {
    const lines = [],
      ticks = [];
    let bars = 0;

    if (!!data.series) {
      const last = _.last(data.series);
      bars = last ? last.x + 1 : 0;
    }

    this.calcMeanModeMedian(lines, ticks, data);

    for (let i = 0; i < this.props.percentiles.length; i++) {
      const pObj = _.find(data.percentiles, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      this.addLine(lines, text, pObj.value);
      this.addTick(ticks, text, pObj.value);
    }

    const newLines = [];
    let lastValue = 0;
    let coll = _(lines)
      .groupBy('value')
      .toPairs()
      .orderBy(l => {
        return +l[0];
      })
      .value();
    _.forEachRight(coll, l => {
      const line = l[1][0];
      const labels = _.map(l[1], l => {
        return l.label.text;
      });
      line.label.text = labels.join(', ');

      const diff = lastValue - +l[0];
      if (diff > 0 && diff < bars * 0.035) {
        line.label.x = -13;
      } else {
        line.label.x = 4;
      }

      newLines.push(line);
      lastValue = +l[0];
    });

    const newTicks = [];
    lastValue = 0;
    coll = _(ticks)
      .groupBy('value')
      .toPairs()
      .orderBy(l => {
        return +l[0];
      })
      .value();
    _.forEachRight(coll, t => {
      const tick = t[1][0];
      if (tick.label) {
        const diff = lastValue - +t[0];
        if (diff > 0 && diff < bars * 0.035) {
          tick.label.x = -20;
        } else {
          tick.label.x = -6;
        }
      }
      if (t[1][1]) {
        const labels = _(t[1])
          .map(l => {
            return l.text;
          })
          .filter(l => !!l)
          .value();
        t[1][1].events = highchartUtils.plotlinesTooltip(labels.join(', '));
        newTicks.push(t[1][1]);
      }

      newTicks.push(tick);
      lastValue = +t[0];
    });

    return newLines.concat(newTicks);
  };

  calcMeanModeMedian(lines, ticks, data) {
    if (!data.series || !data.series.length) {
      return false;
    }

    const mode = math.getModeFromHistogramBy(data.series, 'y', 'x');
    const mean = math.getMeanFromHistogramBy(data.series, 'y', 'x');
    const median = math.getMedianFromHistogramBy(data.series, 'y', 'x');

    this.addLine(lines, 'Mean', mean.toFixed(2));
    this.addLine(lines, 'Median', median);
    this.addLine(lines, 'Mode', mode);

    this.addTick(ticks, 'Mean', mean.toFixed(2));
    this.addTick(ticks, 'Median', median);
    this.addTick(ticks, 'Mode', mode);
  }

  addLine(lines, text, val) {
    lines.push({
      color: '#444444',
      dashStyle: 'dot',
      zIndex: 2,
      width: 2,
      zIndex: 10,
      value: +val,
      label: {
        verticalAlign: 'top',
        rotation: 90,
        text: text,
        style: {
          ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode),
        }
      }
    });
  }

  addTick(ticks, text, value) {
    ticks.push({
      color: '#444444',
      dashStyle: 'dot',
      zIndex: 1,
      width: 2,
      value: +value,
      label: {
        verticalAlign: 'bottom',
        textAlign: 'center',
        rotation: 0,
        x: -6,
        y: 20.5,
        text: `${value}`,
        useHTML: true,
        style: {
          ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode),
          padding: '0 7px',
          backgroundColor: '#fcfcfc',
          color: '#505050',
          fontWeight: 'bold'
        }
      }
    });
    ticks.push({
      color: 'transparent',
      width: 4,
      zIndex: 2,
      value,
      text
    });
  }

  componentDidMount() {
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    chartConfig.exporting = this.props.chartExporting;
    masterchartConfig.chart.events.selection = this.onSelection;

    this.master = new Highcharts['Chart'](`${this.props.id}-master`, masterchartConfig);
    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data && this.props.data.length) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.updateChart();
    }

    if (prevProps.details != this.props.details) {
      this.updateDetails(this.props.details);
    }

    if (prevProps.timeUnit !== this.props.timeUnit) {
      this.props.getDetailsData(this.props.dates);
    }

    if (prevProps.percentiles != this.props.percentiles) {
      this.chart.update({
        xAxis: {
          plotLines: this.calculatePercentiles(this.props.details)
        }
      });
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.master.reflow();
      this.chart.reflow();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Throughput Histogram',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },          
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
    this.master.destroy();
  }

  updateChart() {
    this.master.update({
      xAxis: {
        plotBands: [
          {
            from: this.props.data.length ? this.props.data[0].x : 0,
            to: this.props.data.length ? this.props.data[this.props.data.length - 1].x : 0
          }
        ]
      },
      series: [
        {
          data: _(this.props.data)
            .flatMap('data')
            .sortBy('x')
            .value()
        }
      ]
    });

    if (this.props.details.series) {
      this.updateDetails(this.props.details);
    }
  }

  updateTimeLabelsFormat(dateFormat) {
    const config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.master.update(config);
  }

  updateDetails(data) {
    if (this.props.data.length) {
      let xText, yText;
      switch (this.props.timeUnit) {
        case '2weeks': {
          xText = `Throughput (Number of work items completed on 2 weeks)`;
          yText = `Frequency (Number of 2 Weeks)`;
          break;
        }
        default: {
          xText = `Throughput (Number of work items completed on a ${this.props.timeUnit})`;
          yText = `Frequency (Number of ${this.props.timeUnit}s)`;
          break;
        }
      }

      this.chart.update(
        {
          xAxis: {
            title: {
              text: xText
            },
            plotLines: this.calculatePercentiles(data)
          },
          yAxis: {
            title: {
              text: yText
            }
          },
          series: [
            {
              data: data.series
            }
          ]
        },
        true,
        false,
        false
      );
    } else {
      this.chart.update({
        series: [
          {
            data: []
          }
        ]
      });
    }
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('th', this.props.wallmode);
    this.chart.update(newConfig.chart);
    this.master.update(newConfig.master);
  }

  onSelection(e) {
    var extremesObject = e.xAxis[0],
      min = extremesObject.min,
      max = extremesObject.max,
      xAxis = e.target.xAxis[0];

    xAxis.removePlotBand('mask-before');
    xAxis.addPlotBand({
      id: 'mask-before',
      from: xAxis.dataMin,
      to: min,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1
    });

    xAxis.removePlotBand('mask-after');
    xAxis.addPlotBand({
      id: 'mask-after',
      from: max,
      to: xAxis.dataMax,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1
    });

    if (this.master.resetBtn) {
      this.master.resetBtn.destroy();
    }
    this.master.resetBtn = this.master.renderer
      .button(
        'Reset zoom',
        null,
        null,
        () => {
          this.resetExtremes();
        },
        {
          zIndex: 20
        }
      )
      .attr({
        align: 'right',
        title: 'Reset zoom level 1:1'
      })
      .add(this.master.zoomGroupButton)
      .align(
        {
          align: 'right',
          x: 5,
          y: 10
        },
        false,
        null
      );

    const from = moment(min),
      to = moment(max);
    this.props.getDetailsData({
      startDate: from,
      endDate: to
    });

    return false;
  }

  resetExtremes() {
    this.master.xAxis[0].removePlotBand('mask-before');
    this.master.xAxis[0].removePlotBand('mask-after');
    this.props.getDetailsData(this.props.dates);
    this.master.resetBtn.destroy();
    delete this.master.resetBtn;
  }

  render() {
    return (
      <div className="chart_content chart_tooltip_style">
        <div
          id={`${this.props.id}-details`}
          className={'chart_details ' + (!this.props.showNavigator ? 'chart_details-full ' : ' ')}
        ></div>
        <div
          style={{ display: this.props.showNavigator ? 'block' : 'none' }}
          id={`${this.props.id}-master`}
          className="chart_master"
        ></div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getDetailsData: dates => dispatch(getDetailsData(dates))
  };
};

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.th.dateFormat,
    percentiles: state.filters.th.selectedPercentiles,
    showNavigator: state.filters.th.showNavigator,
    timeUnit: state.filters.th.timeUnit,
    details: state.throughputHistogram.details,
    wallmode: state.app.wallmode
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(ThroughputHistogramChart));
